// apps/client/src/game/commands/items.ts (M1-T13) — pick up, drop, move between pockets and storage, release a fish, and the
// Tool button (bus 'press:tool') cycling hands-free → rod → shovel with the real model in your hand.
import { getEntry, type CatalogId } from '@whiskerton/content';
import type { ItemId, TileXY, Vec2 } from '@whiskerton/shared';
import type { CommandFactory, Game } from '../game';
import { useGame } from '../../state/store';
import { itemsLayerOf } from '../../world/items';

export interface ItemCommands {
  pickUp(id: ItemId): Promise<void>; dropItem(id: ItemId): Promise<void>; moveItem(id: ItemId, to: 'pockets' | 'storage', slot: number | null): Promise<void>;
  releaseFish(id: ItemId): Promise<void>; cycleTool(): Promise<void>; holdTool(catalogId: CatalogId | null): Promise<void>;
}
// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- declaration merging into GameCommands
declare module '../game' { interface GameCommands extends ItemCommands {} }

const TOOL_ORDER = ['rod', 'shovel'] as const;
export function nextTool(owned: readonly CatalogId[], current: CatalogId | null): CatalogId | null {
  const kinds = TOOL_ORDER.map((k) => owned.find((id) => { const e = getEntry(id); return e?.kind === 'tool' && e.tool === k; }) ?? null).filter((x): x is CatalogId => x !== null);
  if (!kinds.length) return null;
  const i = current ? kinds.indexOf(current) : -1;
  if (current && i === -1) return kinds[0]!;
  return i + 1 < kinds.length ? kinds[i + 1]! : null;
}
/** Front tile (facing f, D-07), then your own tile, then the 8 neighbours nearest first. */
export function dropTileFor(pos: Vec2, f: number, free: (t: TileXY) => boolean): TileXY | null {
  const own = { x: Math.floor(pos.x), y: Math.floor(pos.y) };
  const front = { x: Math.floor(pos.x + Math.sin(f) * 0.9), y: Math.floor(pos.y + Math.cos(f) * 0.9) };
  const ring = [[0, 1], [1, 0], [-1, 0], [0, -1], [1, 1], [-1, 1], [1, -1], [-1, -1]].map(([dx, dy]) => ({ x: own.x + dx!, y: own.y + dy! }));
  return [front, own, ...ring].find((t) => free(t) && Math.hypot(t.x + 0.5 - pos.x, t.y + 0.5 - pos.y) <= 1.5) ?? null;
}

export const commands: CommandFactory<ItemCommands> = (game: Game) => {
  const cmds: ItemCommands = {
    async pickUp(id) {
      const layer = itemsLayerOf(game);
      game.you()?.avatar.play('pickUp');
      const res = await game.cmd.act('item.pickUp', { item: id }, { optimistic: () => { layer?.setHidden(id, true); return () => layer?.setHidden(id, false); } });
      if (res.ok) game.sound.sfx('pickUp');
    },
    async dropItem(id) {
      const you = game.you();
      if (!you || you.space !== 'out') return;
      const layer = itemsLayerOf(game);
      const tile = dropTileFor(you.pos, you.f, (t) => !(layer?.occupied(t) ?? false) && (game.scenes.current?.walkable({ x: t.x + 0.5, y: t.y + 0.5 }) ?? false));
      if (!tile) { useGame.getState().toast('reject.blocked_tile'); return; }
      const res = await game.cmd.act('item.drop', { item: id, at: tile });
      if (res.ok && useGame.getState().tool && !useGame.getState().you?.items.some((i) => i.catalogId === useGame.getState().tool)) await cmds.holdTool(null);
    },
    async moveItem(id, to, slot) { await game.cmd.act('item.move', { item: id, to, slot }); },
    async releaseFish(id) { const res = await game.cmd.act('fish.release', { item: id }); if (res.ok) game.sound.sfx('splash'); },
    async cycleTool() {
      const s = useGame.getState();
      const owned = (s.you?.items ?? []).filter((i) => i.at.loc === 'pockets').map((i) => i.catalogId);
      await cmds.holdTool(nextTool(owned, s.tool));
    },
    async holdTool(catalogId) {
      useGame.getState().setTool(catalogId);
      const you = game.you();
      if (!you) return;
      const model = catalogId ? await game.assets.model(catalogId) : null;
      if (useGame.getState().tool !== catalogId || game.you()?.avatar !== you.avatar) return;   // pressed again while the model loaded
      you.avatar.hold(model, 'right');
      game.sound.sfx('swish');
    },
  };
  game.bus.on('press:tool', () => { void cmds.cycleTool(); });
  return cmds;
};
